import { App, Spin } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import {
  TASK_PRIORITY_LABELS,
  TASK_STATUS_LABELS,
  taskPriorityTagClass,
  taskStatusTagClass,
} from '@/constants/task';
import { ApiError } from '@/services/api/client';
import * as tasksApi from '@/services/api/tasks';
import type { TaskDetail } from '@/types/task';
import { formatDateTime } from '@/utils/format-date';

export function LeaderTaskDetailMobilePage() {
  const { taskId } = useParams<{ projectId: string; taskId: string }>();
  const { message } = App.useApp();

  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState<TaskDetail | null>(null);

  const load = useCallback(async () => {
    if (!taskId) {
      return;
    }
    setLoading(true);
    try {
      const detail = await tasksApi.fetchTask(taskId);
      setTask(detail);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载任务详情失败';
      message.error(text);
      setTask(null);
    } finally {
      setLoading(false);
    }
  }, [message, taskId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!taskId) {
    return <MobileEmpty message="无效的任务" />;
  }

  if (loading) {
    return (
      <div className="mobile-card" style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (!task) {
    return <MobileEmpty message="任务不存在或无权查看" />;
  }

  const userAssignments = (task.assignments ?? []).filter((a) => a.assignType === 'USER' && a.user);
  const deptAssignments = (task.assignments ?? []).filter(
    (a) => a.assignType === 'DEPARTMENT' && a.department,
  );

  return (
    <div className="mobile-detail-page">
      <div className="mobile-card mobile-card--flat">
        <div className="mobile-card__header" style={{ marginBottom: 8 }}>
          <span className="mobile-card__title">{task.title}</span>
          <span className={taskStatusTagClass(task.status)}>{TASK_STATUS_LABELS[task.status]}</span>
        </div>
        <div className="mobile-task-card__tags" style={{ marginBottom: 12 }}>
          <span className={taskPriorityTagClass(task.priority)}>
            {TASK_PRIORITY_LABELS[task.priority]}
          </span>
          {task.overdue ? <span className="mobile-tag mobile-tag--danger">已逾期</span> : null}
        </div>

        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">所属项目</span>
          <span className="mobile-detail-row__value">{task.project?.name ?? '—'}</span>
        </div>
        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">任务负责人</span>
          <span className="mobile-detail-row__value">{task.owner?.name ?? '—'}</span>
        </div>
        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">截止时间</span>
          <span className="mobile-detail-row__value">{formatDateTime(task.dueAt)}</span>
        </div>
        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">创建人</span>
          <span className="mobile-detail-row__value">{task.createdBy?.name ?? '—'}</span>
        </div>
        {task.completedAt ? (
          <div className="mobile-detail-row">
            <span className="mobile-detail-row__label">完成时间</span>
            <span className="mobile-detail-row__value">{formatDateTime(task.completedAt)}</span>
          </div>
        ) : null}
        {task.cancelledAt ? (
          <div className="mobile-detail-row">
            <span className="mobile-detail-row__label">取消时间</span>
            <span className="mobile-detail-row__value">{formatDateTime(task.cancelledAt)}</span>
          </div>
        ) : null}
      </div>

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">任务内容</span>
        </div>
        <div className="mobile-card mobile-card--flat">
          <p className="mobile-detail-text">{task.content}</p>
        </div>
      </section>

      {task.completionNote ? (
        <section className="mobile-section">
          <div className="mobile-section__header">
            <span className="mobile-section__title">完成备注</span>
          </div>
          <div className="mobile-card mobile-card--flat">
            <p className="mobile-detail-text">{task.completionNote}</p>
          </div>
        </section>
      ) : null}

      {task.cancelReason ? (
        <section className="mobile-section">
          <div className="mobile-section__header">
            <span className="mobile-section__title">取消原因</span>
          </div>
          <div className="mobile-card mobile-card--flat">
            <p className="mobile-detail-text">{task.cancelReason}</p>
          </div>
        </section>
      ) : null}

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">派发范围</span>
        </div>
        <div className="mobile-card mobile-card--flat">
          {deptAssignments.length === 0 && userAssignments.length === 0 ? (
            <p className="mobile-hint">暂无派发信息</p>
          ) : (
            <>
              {deptAssignments.map((item) => (
                <div key={item.id} className="mobile-detail-row">
                  <span className="mobile-detail-row__label">部门</span>
                  <span className="mobile-detail-row__value">{item.department?.name}</span>
                </div>
              ))}
              {userAssignments.map((item) => (
                <div key={item.id} className="mobile-detail-row">
                  <span className="mobile-detail-row__label">人员</span>
                  <span className="mobile-detail-row__value">{item.user?.name}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
